import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { Button } from '@/components/elements/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/elements/select'
import { SlidePanel } from '@/components/SlidePanel'
import {
  addOrganization,
  addOrganizationRole,
  getOrganizationRoles,
  updateOrganization,
} from '@/services/organization.service'
import { toast } from '@/utils/helpers'

const DEFAULT_VALUES = {
  name: '',
  email: '',
  phoneNumber: '',
  streetAddress: '',
  streetAddress2: '',
  city: '',
  state: '',
  zipCode: '',
  // Create-only: seeds the org Owner + sends an invite.
  ownerEmail: '',
}

const ORG_ROLES = ['Admin', 'Owner']

/**
 * Members manager (edit mode only). Lists current org Owners/Admins and adds
 * new ones by email. Removal is intentionally omitted here (follow-up).
 */
const MembersSection = ({ organizationId }) => {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('Admin')

  const rolesQueryKey = ['organization-roles', organizationId]
  const { data: roles } = useQuery({
    queryKey: rolesQueryKey,
    queryFn: () => getOrganizationRoles({ organizationId }),
    placeholderData: keepPreviousData,
  })

  const { isPending, mutate: addMember } = useMutation({
    mutationFn: () =>
      addOrganizationRole({ organizationId, newUserData: { email }, roles: [role] }),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: () => {
      toast('Member added', 'success')
      setEmail('')
      queryClient.invalidateQueries({ queryKey: rolesQueryKey })
    },
  })

  return (
    <div className="flex flex-col gap-3">
      <span className="leading-6 font-bold">Members</span>

      <ul className="divide-border divide-y rounded-md border">
        {(roles?.results ?? []).length === 0 ? (
          <li className="text-muted-foreground p-3 text-sm">No members yet.</li>
        ) : (
          (roles?.results ?? []).map((member) => (
            <li key={member.id} className="flex items-center justify-between p-3 text-sm">
              <span>{member.user?.email ?? member.user}</span>
              <span className="text-muted-foreground">{(member.roles ?? []).join(', ')}</span>
            </li>
          ))
        )}
      </ul>

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <FormLabel className="mb-1 block">Add member by email</FormLabel>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="person@example.com"
            disabled={isPending}
          />
        </div>
        <Select value={role} onValueChange={setRole} disabled={isPending}>
          <SelectTrigger className="w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ORG_ROLES.map((r) => (
              <SelectItem key={r} value={r}>
                {r}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="button" disabled={!email || isPending} onClick={() => addMember()}>
          Add
        </Button>
      </div>
    </div>
  )
}

MembersSection.propTypes = {
  organizationId: PropTypes.string.isRequired,
}

const AddEditOrganizationSlideout = ({ editOrganization, resetAndClose }) => {
  const isEdit = Boolean(editOrganization)

  const defaultValues = useMemo(() => {
    if (!editOrganization) return DEFAULT_VALUES
    const { name, email, phoneNumber, streetAddress, streetAddress2, city, state, zipCode } =
      editOrganization
    return {
      ...DEFAULT_VALUES,
      name: name || '',
      email: email || '',
      phoneNumber: phoneNumber || '',
      streetAddress: streetAddress || '',
      streetAddress2: streetAddress2 || '',
      city: city || '',
      state: state || '',
      zipCode: zipCode || '',
    }
  }, [editOrganization])

  const form = useForm({ defaultValues })

  const resetForm = () => {
    form.reset(DEFAULT_VALUES)
    resetAndClose()
  }

  const queryClient = useQueryClient()
  const { isPending, mutate: mutateOrganization } = useMutation({
    mutationFn: (data) =>
      isEdit ? updateOrganization({ id: editOrganization.id, ...data }) : addOrganization(data),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: (data) => {
      toast(`${data.name} ${isEdit ? 'updated' : 'added'} successfully`, 'success')
      resetForm()
      queryClient.invalidateQueries({ queryKey: ['organizations'] })
    },
  })

  const onSubmit = async (data) => {
    // Drop empty owner fields so the API doesn't try to seed a blank owner.
    const payload = { ...data }
    if (!payload.ownerEmail) delete payload.ownerEmail
    mutateOrganization(payload)
  }

  return (
    <SlidePanel
      actions={[
        { label: 'Cancel', onClick: () => resetAndClose(), type: 'cancel' },
        {
          label: `${isEdit ? 'Update' : 'Add'} Organization`,
          onClick: form.handleSubmit(onSubmit),
          type: 'submit',
        },
      ]}
      content={
        <Form {...form}>
          <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-4">
              <span className="leading-6 font-bold">Organization Details</span>

              <FormField
                control={form.control}
                disabled={isPending}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                rules={{ required: 'This field is required' }}
              />

              <FormField
                control={form.control}
                disabled={isPending}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                disabled={isPending}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input type="tel" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {!isEdit && (
              <div className="flex flex-col gap-4">
                <span className="leading-6 font-bold">Owner</span>
                <FormField
                  control={form.control}
                  disabled={isPending}
                  name="ownerEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Owner Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="owner@example.com" {...field} />
                      </FormControl>
                      <p className="text-muted-foreground text-xs">
                        Optional. Seeds the organization Owner and emails them an invite. You can
                        add more members after creating.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {isEdit && <MembersSection organizationId={editOrganization.id} />}
          </form>
        </Form>
      }
      loading={isPending}
      title={`${isEdit ? 'Update' : 'Add'} Organization`}
      setOpen={resetForm}
    />
  )
}

AddEditOrganizationSlideout.propTypes = {
  editOrganization: PropTypes.object,
  resetAndClose: PropTypes.func.isRequired,
}

export default AddEditOrganizationSlideout
